import React, { useState } from 'react'
import './styles/notification-builder-preview.scss'

import { Select, Well } from '@pushly/aqe/lib/components'
import { Button } from '@pushly/aqe/lib/components'
import { getClassNames } from '../../../_utils/classnames'
import { extractVariantPreview } from '../helpers'
import { DomainDto } from '../../../dtos/domain'
import { UserDto } from '../../../dtos/user'
import PreviewSDK, { Platform } from '@pushly/cuttlefish'
import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons'
import { Popover, Typography } from 'antd'
import { getPlatformNotifications } from '../../../_utils/platform'
import { NotificationVariantModel } from '../../../models/notification/notification-variant.model'
import { NotificationBuilderSubmitType } from '../enums'
import { getPushlySdk } from '../../../_utils/pushly-sdk'
import { pause, simpleNotification } from '../../../_utils/utils'
import { UserService } from '../../../services'
import { Container } from 'typescript-ioc/es5'
import { useService } from '../../../hooks/use-service'
import { AppState } from '../../../stores/app'
import { AbilityAction } from '../../../enums/ability-action.enum'
import { SubjectEntity } from '../../../enums/ability-entity.enum'
import { IBetterSelectOption } from '../../better-select/interfaces'
import { usePersistedNotificationPreviewState } from '../../../stores/notification-preview-state'
import { observer } from 'mobx-react'
import { coalesce } from '../../../_utils/coalesce'
import { DeliveryChannel, Macro, strIsKnownMacro } from '@pushly/models/lib/enums'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { PreviewPlatform } from '@pushly/models/lib/enums/preview-platform'
import { isDeliveryChannelEnabled } from '../../../_utils/domain'
import { FEAT_CHANNEL_ANDROID, FEAT_CHANNEL_IOS } from '../../../constants'
import { useThemeMode } from '@pushly/aqe'

interface INotificationBuilderPreview {
    level: 'org' | 'domain'
    levelId: number
    domain: DomainDto
    source: NotificationVariantModel
    options?: IBetterSelectOption[]
    loading?: boolean
    hideInlinePreview?: boolean
    hideDevicePreview?: boolean
    chromeless?: boolean
    isInDrawer?: boolean
    onPreviewRequest?: (type: NotificationBuilderSubmitType) => void
}

const WEB_PLATFORMS = [PreviewPlatform.ANDROID, PreviewPlatform.MAC_OS, PreviewPlatform.WINDOWS]

export async function registerUserPuuid(user: UserDto, domain: DomainDto): Promise<void> {
    try {
        const sdk = getPushlySdk()
        if (!sdk) {
            simpleNotification('error', 'We are unable to send you a preview notification at this time.')
            return
        }

        const sdkUser = sdk.context.user

        const currentRegistrations: any[] = user.puuids || []

        const permission = await new Promise<string>(async (res) => {
            // this is referring to system prompts / cannot use PromptStyle enum due to SDK style implementation
            if (!sdkUser?.isSubscribed) {
                sdk.on('permission_allowed', async () => {
                    // Allow 3 secs for ES doc to be updated
                    await pause(3000)
                    res('subscribed')
                })

                sdk.on('permission_dismissed', () => res('dismissed'))
                sdk.on('permission_denied', () => res('denied'))

                // Safety net: if the SDK determines the prompt can't be shown
                // (e.g., iOS non-standalone, unsupported browser), resolve immediately
                // rather than hanging the Promise forever.
                window.pushly?.('on_prompt_ineligible', () => res('ineligible'))

                sdk.PushNotifications.showPermissionPrompt()
            } else {
                res('subscribed')
            }
        })

        if (permission === 'subscribed') {
            const userSvc: UserService = Container.get(UserService)

            const existingRegistration = currentRegistrations.find(
                (r) => r.puuid === sdkUser.puuid && r.domainId === domain.id,
            )

            if (!existingRegistration) {
                await userSvc.registerPuuid(user.id, domain.id, sdkUser.puuid)
            }
        } else if (permission === 'denied') {
            simpleNotification(
                'error',
                `We are unable to send you a preview 
                    of this notification because you have denied notification 
                    permissions. Please manually reset your browser permissions 
                    and attempt to send the preview again.`,
            )
        } else if (permission === 'ineligible') {
            simpleNotification(
                'error',
                'Push notifications are not available in this browser context. If you are on iOS, please add this app to your home screen first.',
            )
        }
    } catch (_) {}
}

export enum PreviewState {
    COLLAPSED = 'collapsed',
    EXPANDED = 'expanded',
}

const NotificationBuilderPreview = observer((props: INotificationBuilderPreview) => {
    const appState = useService<AppState>(AppState)
    const { resolvedMode } = useThemeMode()

    const isDomainLevel = props.level === 'domain'
    const userPreferences = useUserPreferences(
        appState.currentUser?.id,
        isDomainLevel ? props.levelId : undefined,
        !isDomainLevel ? props.levelId : undefined,
    )
    const notificationPreviewState = usePersistedNotificationPreviewState()

    const { currentUser, currentDomain } = appState
    const {
        domain,
        source,
        options,
        loading,
        hideInlinePreview,
        hideDevicePreview,
        chromeless,
        isInDrawer = false,
        onPreviewRequest,
    } = props

    const npsCache = notificationPreviewState.value.getCache(props.level, props.levelId)
    const userNpsPreferences = userPreferences.get(['notification_preview_state'])
    const PREVIEW_BATCH_KEY = 'preview-settings'

    const setSelectedPreviewPlatform = (value: PreviewPlatform) => {
        if (userNpsPreferences) {
            setPreviewPlatform(value)
            userPreferences.setDebounced(['notification_preview_state', 'platform'], value, {
                batchKey: PREVIEW_BATCH_KEY,
                delay: 1000,
            })
        } else if (value && npsCache.platform !== value) {
            notificationPreviewState.value.isReady.then((nps) => {
                npsCache.platform = value
            })
        }
    }

    const setSelectedPreviewState = (value: PreviewState) => {
        if (userNpsPreferences) {
            setPreviewState(value)
            userPreferences.setDebounced(['notification_preview_state', 'state'], value, {
                batchKey: PREVIEW_BATCH_KEY,
                delay: 1000,
            })
        } else if (value && npsCache.state !== value) {
            notificationPreviewState.value.isReady.then((nps) => {
                npsCache.state = value
            })
        }
    }

    let userSubStatus = 'default'
    const platformNotifications = getPlatformNotifications()
    const sdk = getPushlySdk()
    if (sdk) {
        userSubStatus = sdk.context?.user?.subscriberState
    }

    const canSendPreview =
        platformNotifications.supported &&
        appState.abilityStore.can(
            AbilityAction.CREATE,
            appState.abilityStore.getDomainOwnedIdentityFor(SubjectEntity.NOTIFICATION),
        )

    const SubStatusAllowList = ['default', 'subscribed', 'dismissed']
    const dismissPreviewAction = userSubStatus === 'dismissed'
    const disablePreviewAction = SubStatusAllowList.indexOf(userSubStatus) === -1

    const domainIsWebEnabled = isDeliveryChannelEnabled(domain, DeliveryChannel.WEB)
    const domainHasNativeIOS = domain.flags.includes(FEAT_CHANNEL_IOS)
    const domainHasNativeAndroid = domain.flags.includes(FEAT_CHANNEL_ANDROID)

    // Build default preview platform options based on domain capabilities
    const defaultOptions: { value: PreviewPlatform | Platform; label: string }[] = []
    if (domainIsWebEnabled) {
        defaultOptions.push({
            value: Platform.ANDROID,
            label: 'Android',
        })
    }
    if (domainHasNativeAndroid) {
        defaultOptions.push({
            value: Platform.NATIVE_ANDROID,
            label: 'Android (Native)',
        })
    }
    if (domainHasNativeIOS) {
        defaultOptions.push({
            value: Platform.IOS,
            label: 'Apple / iOS',
        })
    }
    if (domainIsWebEnabled) {
        defaultOptions.push({
            value: Platform.MAC_OS,
            label: 'MacOS',
        })
    }
    if (domainIsWebEnabled) {
        defaultOptions.push({
            value: Platform.WINDOWS,
            label: 'Web / Windows',
        })
    }

    // Handle a scenario where the default (global) platform is a web-enabled platform
    // but the domain does not support web notifications.
    let defaultSelectedPreviewPlatform = userNpsPreferences?.platform
    if (!domainIsWebEnabled && WEB_PLATFORMS.includes(defaultSelectedPreviewPlatform)) {
        if (domainHasNativeAndroid) {
            defaultSelectedPreviewPlatform = PreviewPlatform.NATIVE_ANDROID
        } else if (domainHasNativeIOS) {
            defaultSelectedPreviewPlatform = PreviewPlatform.IOS
        }
    }

    const [previewPlatform, setPreviewPlatform] = useState<PreviewPlatform>(
        npsCache.resolvePlatformFromAvailableChannels(source.getChannels(), defaultSelectedPreviewPlatform),
    )
    const [previewState, setPreviewState] = useState<PreviewState>(
        userNpsPreferences?.state ?? coalesce(npsCache.state, PreviewState.COLLAPSED),
    )

    const activePreviewConfig = extractVariantPreview(domain, source, previewPlatform, previewState, false)
    const selectedVariantImageUrl = source?.getContent()?.getDefaultContent()?.getImageUrl() ?? ''

    return !notificationPreviewState.isLoaded ? null : (
        <>
            <Well
                className={getClassNames('notification-builder-preview', 'nested', {
                    'no-inline-preview': hideInlinePreview,
                    'no-device-preview': hideDevicePreview,
                    'has-ecomm-image-macro': strIsKnownMacro(selectedVariantImageUrl, {
                        exact: true,
                        allowedMacros: [Macro.ECOMM_ITEM_IMAGE],
                    }),
                    'has-recommendation-image-macro': strIsKnownMacro(selectedVariantImageUrl, {
                        exact: true,
                        allowedMacros: [Macro.ARTICLE_IMAGE],
                    }),
                })}
                disabled={loading}
                footerMode="hidden"
                mode={chromeless ? 'ghost' : undefined}
                variant={isInDrawer || chromeless ? 'ghost' : 'default'}
                padding={isInDrawer || chromeless ? 'none' : 'base'}
                title={
                    <span>
                        <span className="label-with-component-r">Preview</span>
                        <Popover
                            styles={{
                                root: { maxWidth: 400 },
                            }}
                            classNames={{
                                content: getClassNames('notification-builder-popover', 'inline-preview-popover'),
                            }}
                            content={
                                <>
                                    <Typography.Paragraph>
                                        The platform preview is a best-guess at how this notification will look across
                                        the most common devices for the selected operating system.
                                    </Typography.Paragraph>

                                    <Typography.Paragraph>
                                        Due to variability between devices this preview may not accurately depict title
                                        & body length limitations or image crop & placement. A more accurate preview may
                                        be obtained via a notification preview on your browser or mobile device by using
                                        the Device Preview.
                                    </Typography.Paragraph>
                                </>
                            }
                        >
                            <InfoCircleOutlined className="info-icon" />
                        </Popover>
                    </span>
                }
            >
                <div className="preview-select">
                    <Select
                        prefix="Platform:"
                        aria-label="Preview platform"
                        showSearch={false}
                        value={[previewPlatform]}
                        onChange={(val: PreviewPlatform | Platform) => {
                            switch (val) {
                                case Platform.MAC_OS:
                                case PreviewPlatform.MAC_OS:
                                    setSelectedPreviewState(PreviewState.COLLAPSED)
                                    break
                                case Platform.ANDROID:
                                case Platform.NATIVE_ANDROID:
                                case PreviewPlatform.ANDROID:
                                case PreviewPlatform.NATIVE_ANDROID:
                                    setSelectedPreviewState(PreviewState.EXPANDED)
                                    break
                            }
                            if (val && Object.values(PreviewPlatform).includes(val as PreviewPlatform)) {
                                setSelectedPreviewPlatform(val as PreviewPlatform)
                            }
                        }}
                        options={options?.length ? options : defaultOptions}
                        style={{ width: '45%' }}
                    />

                    <Select
                        prefix="State:"
                        aria-label="Preview state"
                        showSearch={false}
                        value={[previewState]}
                        onChange={(v: PreviewState) => {
                            setSelectedPreviewState(v)
                        }}
                        options={[
                            {
                                value: PreviewState.EXPANDED,
                                label: 'Expanded',
                            },
                            {
                                value: PreviewState.COLLAPSED,
                                label: 'Collapsed',
                            },
                        ]}
                        style={{ width: '45%' }}
                    />
                </div>

                {!hideInlinePreview && (
                    <div className="preview-wrapper-container" data-image-url={selectedVariantImageUrl}>
                        <Well
                            className="preview-wrapper"
                            disabled={loading}
                            hideHeader={true}
                            footerMode="hidden"
                            mode="ghost"
                        >
                            <PreviewSDK.Preview
                                config={{ ...activePreviewConfig, systemMode: resolvedMode ?? 'light' }}
                            />
                        </Well>
                    </div>
                )}

                {!hideDevicePreview && (
                    <Well
                        className={getClassNames('notification-builder-device-preview', {
                            unsupported: !platformNotifications.supported,
                        })}
                        disabled={loading}
                        footerMode="hidden"
                        variant="ghost"
                        padding="small"
                        title={
                            <span>
                                <span className="label-with-component-r">Device Preview</span>
                                {!platformNotifications.supported ? (
                                    <WarningOutlined className="info-icon" />
                                ) : (
                                    <Popover
                                        styles={{
                                            root: { maxWidth: 400 },
                                        }}
                                        classNames={{
                                            content: getClassNames(
                                                'notification-builder-popover',
                                                'device-preview-popover',
                                            ),
                                        }}
                                        content={
                                            <>
                                                <Typography.Paragraph>
                                                    A device preview will immediately deliver the selected notification
                                                    to all registered devices for the chosen preview destination.
                                                </Typography.Paragraph>
                                                <Typography.Paragraph>
                                                    You may choose to deliver a preview of the currently selected
                                                    notification to your registered personal devices (Personal Preview)
                                                    or to all internal <span>{domain.displayName} </span>
                                                    users that have registered devices for preview notifications (Team
                                                    Preview).
                                                </Typography.Paragraph>

                                                {platformNotifications.supported ? (
                                                    !dismissPreviewAction ? (
                                                        !disablePreviewAction ? (
                                                            <>
                                                                <Typography.Paragraph>
                                                                    To receive previews on multiple devices you may
                                                                    <br />
                                                                    <a
                                                                        className="register-preview-device-link"
                                                                        href={`${appState.documentationLink}/platform/notifications/notification-previews#registering-additional-preview-devices`}
                                                                        target="_blank"
                                                                    >
                                                                        associate additional devices
                                                                    </a>{' '}
                                                                    with your user.
                                                                </Typography.Paragraph>

                                                                <Typography.Paragraph>
                                                                    Any additional devices you add will be added to your
                                                                    list of personal devices and will receive a
                                                                    notification preview when you choose the 'Personal
                                                                    Preview' destination or someone else sends a 'Team
                                                                    Preview'.
                                                                </Typography.Paragraph>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Typography.Paragraph>
                                                                    Notifications are blocked in the current browser.
                                                                </Typography.Paragraph>
                                                                <Typography.Paragraph>
                                                                    Please manually reset your browser permissions,
                                                                    reload the page, and re-register this device for
                                                                    notification previews.
                                                                </Typography.Paragraph>
                                                            </>
                                                        )
                                                    ) : (
                                                        <>
                                                            <Typography.Paragraph>
                                                                Notifications have been dismissed in the current
                                                                browser.
                                                            </Typography.Paragraph>
                                                            <Typography.Paragraph>
                                                                Please reload the page, and re-register this device for
                                                                notification previews.
                                                            </Typography.Paragraph>
                                                        </>
                                                    )
                                                ) : (
                                                    <Typography.Paragraph>
                                                        Notifications are not supported in the current browser.
                                                    </Typography.Paragraph>
                                                )}
                                            </>
                                        }
                                    >
                                        <InfoCircleOutlined className="info-icon" />
                                    </Popover>
                                )}
                            </span>
                        }
                        actions={
                            canSendPreview && (
                                <div>
                                    <Button
                                        size="small"
                                        onClick={async () => {
                                            await registerUserPuuid(currentUser!, currentDomain!)
                                            return onPreviewRequest?.(NotificationBuilderSubmitType.PERSONAL_PREVIEW)
                                        }}
                                        disabled={false}
                                        actions={[
                                            {
                                                text: 'Send Team Preview',
                                                onClick: async () => {
                                                    await registerUserPuuid(currentUser!, currentDomain!)
                                                    return onPreviewRequest?.(
                                                        NotificationBuilderSubmitType.TEAM_PREVIEW,
                                                    )
                                                },
                                                disabled: false,
                                                disabledMessage:
                                                    'At least one team member must be registered to send a team preview.',
                                            },
                                        ]}
                                    >
                                        <span>Send Personal Preview</span>
                                    </Button>
                                </div>
                            )
                        }
                    >
                        {!platformNotifications.supported && (
                            <Typography.Paragraph>
                                Device previews are currently unavailable. Push Notifications are not supported in the
                                current browser session. Please ensure you are not running in incognito or private mode
                                and that you are not running any ad-blockers.
                            </Typography.Paragraph>
                        )}
                    </Well>
                )}
            </Well>
        </>
    )
})

export default NotificationBuilderPreview
